import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Theme
} from '@mui/material';
import {
    BarChart as BarChartIcon,
    Timeline as TimelineIcon,
    DataObject as DataObjectIcon,
    Analytics as AnalyticsIcon,
    PlayArrow as PlayIcon
} from '@mui/icons-material';
import { API_ENDPOINTS, apiCall } from '../config/api';

interface Topic {
    id: string;
    name: string;
    description: string;
    charts: string[];
}

export const DataScience = () => {
    const [topics, setTopics] = useState<Topic[]>([]);
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTopics();
    }, []);

    const fetchTopics = async () => {
        try {
            const data = await apiCall(API_ENDPOINTS.DATA_SCIENCE_TOPICS);
            setTopics(data.topics);
        } catch (error) {
            console.error('Error fetching topics:', error);
            // Fallback data if API is not available
            setTopics([
                {
                    id: "statistics",
                    name: "Statistics",
                    description: "Descriptive and inferential statistics",
                    charts: ["histogram", "box_plot", "scatter_plot", "correlation_matrix"]
                },
                {
                    id: "data_visualization",
                    name: "Data Visualization",
                    description: "Creating meaningful visualizations",
                    charts: ["line_chart", "bar_chart", "heatmap", "pie_chart"]
                },
                {
                    id: "data_preprocessing",
                    name: "Data Preprocessing",
                    description: "Cleaning and preparing data",
                    charts: ["missing_data", "outlier_detection", "data_distribution"]
                },
                {
                    id: "exploratory_data_analysis",
                    name: "Exploratory Data Analysis",
                    description: "Understanding data patterns and relationships",
                    charts: ["pair_plot", "distribution_plot", "correlation_analysis"]
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const getTopicIcon = (topicId: string) => {
        switch (topicId) {
            case 'statistics':
                return <BarChartIcon />;
            case 'data_visualization':
                return <TimelineIcon />;
            case 'data_preprocessing':
                return <DataObjectIcon />;
            case 'exploratory_data_analysis':
                return <AnalyticsIcon />;
            default:
                return <BarChartIcon />;
        }
    };

    const handleTopicClick = (topicId: string) => {
        setSelectedTopic(selectedTopic === topicId ? null : topicId);
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Loading Data Science Topics...
                </Typography>
            </Container>
        );
    }

    // SVG Graphics Components
    const DataScienceHero = () => (
        <Box sx={{ position: 'relative', width: '100%', height: { xs: 150, sm: 200, md: 250 }, mb: 4, overflow: 'hidden' }}>
            <svg width="100%" height="100%" viewBox="0 0 800 300" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#667eea" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#764ba2" stopOpacity="0.3" />
                    </linearGradient>
                    <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f093fb" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#4facfe" stopOpacity="0.3" />
                    </linearGradient>
                </defs>
                {/* Background circles */}
                <circle cx="100" cy="80" r="60" fill="url(#gradient1)" opacity="0.6" />
                <circle cx="700" cy="200" r="80" fill="url(#gradient2)" opacity="0.6" />
                <circle cx="400" cy="50" r="40" fill="url(#gradient1)" opacity="0.4" />
                
                {/* Chart graphics */}
                <g transform="translate(200, 100)">
                    <rect x="0" y="0" width="8" height="60" fill="#667eea" rx="2" />
                    <rect x="15" y="0" width="8" height="80" fill="#667eea" rx="2" />
                    <rect x="30" y="0" width="8" height="45" fill="#667eea" rx="2" />
                    <rect x="45" y="0" width="8" height="70" fill="#667eea" rx="2" />
                    <rect x="60" y="0" width="8" height="55" fill="#667eea" rx="2" />
                </g>
                
                {/* Line chart */}
                <polyline
                    points="350,180 380,150 410,170 440,120 470,140 500,100 530,130"
                    fill="none"
                    stroke="#4facfe"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                
                {/* Data points */}
                <circle cx="350" cy="180" r="4" fill="#4facfe" />
                <circle cx="410" cy="170" r="4" fill="#4facfe" />
                <circle cx="470" cy="140" r="4" fill="#4facfe" />
                <circle cx="530" cy="130" r="4" fill="#4facfe" />
                
                {/* Pie chart representation */}
                <g transform="translate(600, 120)">
                    <path d="M 0,0 L 40,0 A 40,40 0 0,1 28,28 Z" fill="#f093fb" />
                    <path d="M 0,0 L 28,28 A 40,40 0 0,1 -28,28 Z" fill="#4facfe" />
                    <path d="M 0,0 L -28,28 A 40,40 0 0,1 0,-40 Z" fill="#667eea" />
                </g>
            </svg>
        </Box>
    );

    const TopicCardGraphic = ({ topicId }: { topicId: string }) => {
        const graphics: Record<string, JSX.Element> = {
            statistics: (
                <Box sx={{ position: 'absolute', top: 10, right: 10, opacity: 0.1, width: 80, height: 80 }}>
                    <svg viewBox="0 0 100 100">
                        <rect x="10" y="30" width="15" height="50" fill="currentColor" rx="2" />
                        <rect x="30" y="20" width="15" height="60" fill="currentColor" rx="2" />
                        <rect x="50" y="40" width="15" height="40" fill="currentColor" rx="2" />
                        <rect x="70" y="25" width="15" height="55" fill="currentColor" rx="2" />
                    </svg>
                </Box>
            ),
            data_visualization: (
                <Box sx={{ position: 'absolute', top: 10, right: 10, opacity: 0.1, width: 80, height: 80 }}>
                    <svg viewBox="0 0 100 100">
                        <polyline points="10,80 30,60 50,70 70,40 90,50" fill="none" stroke="currentColor" strokeWidth="2" />
                        <circle cx="30" cy="60" r="3" fill="currentColor" />
                        <circle cx="50" cy="70" r="3" fill="currentColor" />
                        <circle cx="70" cy="40" r="3" fill="currentColor" />
                    </svg>
                </Box>
            ),
            data_preprocessing: (
                <Box sx={{ position: 'absolute', top: 10, right: 10, opacity: 0.1, width: 80, height: 80 }}>
                    <svg viewBox="0 0 100 100">
                        <rect x="10" y="20" width="25" height="25" fill="currentColor" opacity="0.5" />
                        <rect x="40" y="20" width="25" height="25" fill="currentColor" />
                        <rect x="70" y="20" width="25" height="25" fill="currentColor" opacity="0.5" />
                        <rect x="10" y="50" width="25" height="25" fill="currentColor" />
                        <rect x="40" y="50" width="25" height="25" fill="currentColor" opacity="0.5" />
                        <rect x="70" y="50" width="25" height="25" fill="currentColor" />
                    </svg>
                </Box>
            ),
            exploratory_data_analysis: (
                <Box sx={{ position: 'absolute', top: 10, right: 10, opacity: 0.1, width: 80, height: 80 }}>
                    <svg viewBox="0 0 100 100">
                        <circle cx="30" cy="30" r="15" fill="currentColor" opacity="0.6" />
                        <circle cx="70" cy="30" r="15" fill="currentColor" opacity="0.4" />
                        <circle cx="50" cy="70" r="15" fill="currentColor" opacity="0.5" />
                    </svg>
                </Box>
            )
        };
        return graphics[topicId] || graphics.statistics;
    };

    return (
        <Container 
            maxWidth="lg"
            sx={{ 
                px: { xs: 1, sm: 2 }
            }}
        >
            <Box sx={{ position: 'relative', mb: { xs: 3, sm: 4 } }}>
                <DataScienceHero />
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', textAlign: 'center' }}>
                    <Typography 
                        variant="h4" 
                        component="h1" 
                        gutterBottom 
                        align="center" 
                        sx={{ 
                            mb: 1,
                            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                            fontWeight: 'bold',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                        }}
                    >
                        Data Science Topics
                    </Typography>
                    
                    <Typography 
                        variant="body1" 
                        paragraph 
                        align="center" 
                        sx={{ 
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            px: { xs: 2, sm: 4 },
                            maxWidth: '600px',
                            margin: '0 auto',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
                        }}
                    >
                        Explore various data science concepts and techniques with interactive charts and examples.
                    </Typography>
                </Box>
            </Box>

            <Box 
                sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    gap: { xs: 2, sm: 3 },
                    flexWrap: 'wrap'
                }}
            >
                {topics.map((topic) => (
                    <Box 
                        key={topic.id} 
                        sx={{ 
                            flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.333% - 16px)' }
                        }}
                    >
                        <Card 
                            sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                position: 'relative',
                                overflow: 'hidden',
                                background: (theme: Theme) => 
                                    theme.palette.mode === 'dark' 
                                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                                '&:hover': {
                                    transform: { xs: 'translateY(-2px)', sm: 'translateY(-4px)' },
                                    boxShadow: { xs: 2, sm: 4 },
                                    background: (theme: Theme) => 
                                        theme.palette.mode === 'dark' 
                                            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'
                                            : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                                },
                                boxShadow: { xs: 1, sm: 2 }
                            }}
                            onClick={() => handleTopicClick(topic.id)}
                        >
                            <TopicCardGraphic topicId={topic.id} />
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(102, 126, 234, 0.2)' 
                                                : 'rgba(102, 126, 234, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {getTopicIcon(topic.id)}
                                    </Box>
                                    <Typography 
                                        variant='h6' 
                                        component='h2' 
                                        sx={{ 
                                            ml: 1,
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                            fontWeight: '600'
                                        }}
                                    >
                                        {topic.name}
                                    </Typography>
                                </Box>
                                
                                <Typography 
                                    variant='body2' 
                                    color='text.secondary' 
                                    paragraph
                                    sx={{ 
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                        mb: { xs: 2, sm: 3 }
                                    }}
                                >
                                    {topic.description}
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography 
                                        variant='subtitle2' 
                                        gutterBottom
                                        sx={{ 
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            mb: { xs: 1, sm: 1.5 }
                                        }}
                                    >
                                        Available Charts:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {topic.charts.map((chart) => (
                                            <Chip
                                                key={chart}
                                                label={chart.replace('_', ' ')}
                                                size="small"
                                                variant="outlined"
                                                sx={{ 
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                    height: { xs: 20, sm: 24 }
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                <Button
                                    variant="contained"
                                    startIcon={<PlayIcon />}
                                    fullWidth
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        e.stopPropagation();
                                        // TODO: Implement chart generation
                                        console.log(`Generate charts for ${topic.name}`);
                                    }}
                                >
                                    Generate Charts
                                </Button>
                            </CardContent>
                        </Card>
                    </Box>
                ))}
            </Box>

            {selectedTopic && (
                <Paper 
                    sx={{ 
                        mt: 4, 
                        p: 3,
                        position: 'relative',
                        overflow: 'hidden',
                        background: (theme: Theme) => 
                            theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(240, 147, 251, 0.1) 100%)'
                                : 'linear-gradient(135deg, rgba(79, 172, 254, 0.05) 0%, rgba(240, 147, 251, 0.05) 100%)',
                    }}
                >
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, opacity: 0.1 }}>
                        <svg viewBox="0 0 200 200">
                            <circle cx="100" cy="100" r="80" fill="currentColor" />
                            <circle cx="100" cy="100" r="50" fill="none" stroke="currentColor" strokeWidth="2" />
                        </svg>
                    </Box>
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {topics.find(t => t.id === selectedTopic)?.name} - Details
                        </Typography>
                        <Typography variant="body1" paragraph>
                            This section will contain detailed information about {selectedTopic} including:
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(102, 126, 234, 0.2)' 
                                                : 'rgba(102, 126, 234, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <BarChartIcon />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText primary="Interactive examples and tutorials" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(102, 126, 234, 0.2)' 
                                                : 'rgba(102, 126, 234, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <TimelineIcon />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText primary="Real-world use cases and applications" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(102, 126, 234, 0.2)' 
                                                : 'rgba(102, 126, 234, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <DataObjectIcon />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText primary="Code examples and implementations" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(102, 126, 234, 0.2)' 
                                                : 'rgba(102, 126, 234, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <AnalyticsIcon />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText primary="Best practices and tips" />
                            </ListItem>
                        </List>
                    </Box>
                </Paper>
            )}
        </Container>
    );
};
